import { useEffect, useState } from 'react';
import { useSchedule } from '../../../../context/ScheduleContext';
import usePostalCodeResult from '../../../../hooks/usePotalCodeResult';
import { Order } from '../../../../types/types';
import Loading from '../../../Loading';
import PostalCodeForm from '../../PostalCodeForm';
import ScheduleCalendar from '../../ScheduleCalendar';
import StateCityNeightborn from '../../StateCityNeightborn';
import {
  ButtonSearchType,
  ContainerCol,
  Content,
  ContentCol,
  LocalesList,
  LocalesListItem,
  Title,
} from './styles';

interface ScheduleLocalesProps {
  order: Order | null;
  isVideoConference: string;
}

const DISTANCE_FALLBACK_KM = 9999;

/** 0, null ou inválido → 9999 (ordena por último). */
const distanceKm = (d: number | null | undefined) => {
  const n = Number(d);
  return d != null && Number.isFinite(n) && n > 0 ? n : DISTANCE_FALLBACK_KM;
};

const ScheduleLocales = ({ order, isVideoConference }: ScheduleLocalesProps) => {
  const [searchType, setSearchType] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const filterVideoConference = isVideoConference === 'videoconference';
  const [local, setLocal] = useState<number>();
  const { locales, isLoading, error, handlePostalCodeSearch, resetSearchResults } =
    usePostalCodeResult(postalCode, order?.id, filterVideoConference);

  const filteredLocales = locales.filter(
    (locale) => (String(locale.videoconferencia) === 'true') === filterVideoConference
  );
  const [shouldFetchSchedules, setShouldFetchSchedules] = useState(false);
  const [cityPanelKey, setCityPanelKey] = useState(0);
  const { setData } = useSchedule();

  const handleSearchType = (type: string) => {
    const nextIsPostal = type === 'postalCode';
    const currentIsPostal = searchType === 'postalCode' || searchType === '';

    if (nextIsPostal && currentIsPostal) {
      if (searchType === '') setSearchType('postalCode');
      return;
    }

    if (type === 'city' && searchType === 'city') return;

    resetSearchResults();
    setPostalCode('');
    setLocal(undefined);
    setShouldFetchSchedules(false);
    setData((prev) => ({
      ...prev,
      posto: null,
      detalhePosto: {
        ...prev.detalhePosto,
        codigo: null,
        nomeFantasia: '',
        telefone: null,
        endereco: '',
        numero: null,
        complemento: '',
        cidade: '',
        uf: '',
        cep: null,
        bairro: '',
      },
    }));

    if (!nextIsPostal) {
      setCityPanelKey((k) => k + 1);
    }

    setSearchType(nextIsPostal ? 'postalCode' : 'city');
  };

  useEffect(() => {
    if (local && locales) {
      const selectedLocale = locales.find((locale) => locale.codigo === local);

      if (selectedLocale) {
        setData((prevState) => {
          return {
            ...prevState,
            idGar: order?.id,
            pedido: order?.id,
            posto: selectedLocale.codigo,
            detalhePosto: {
              ...prevState.detalhePosto,
              codigo: selectedLocale.codigo,
              nomeFantasia: selectedLocale.nomeFantasia,
              telefone: selectedLocale.telefone,
              endereco: selectedLocale.endereco,
              numero: selectedLocale.numero,
              complemento: selectedLocale.complemento || '',
              cidade: selectedLocale.cidade,
              uf: selectedLocale.uf,
              cep: selectedLocale.cep,
              bairro: selectedLocale.bairro,
            },
          };
        });
      }
    }
  }, [local, locales, order, setData]);

  useEffect(() => {
    if (shouldFetchSchedules && postalCode) {
      handlePostalCodeSearch();
      setShouldFetchSchedules(false);
    }
  }, [postalCode, shouldFetchSchedules, handlePostalCodeSearch]);

  return (
    <ContainerCol>
      <Title>Informe sua localização para acessar as agendas</Title>
      <Content className="container-top">
        <ButtonSearchType
          $isActive={searchType === 'postalCode' || searchType === ''}
          onClick={() => handleSearchType('postalCode')}
        >
          Buscar por CEP
        </ButtonSearchType>
        <ButtonSearchType
          $isActive={searchType === 'city'}
          onClick={() => handleSearchType('city')}
        >
          Buscar por cidade
        </ButtonSearchType>
      </Content>
      <ContentCol>
        {(searchType === 'postalCode' || searchType === '') && (
          <PostalCodeForm
            postalCode={postalCode}
            error={error}
            handlePostalCodeChange={setPostalCode}
            handlePostalCodeSearch={handlePostalCodeSearch}
          />
        )}
        {searchType === 'city' && (
          <StateCityNeightborn
            key={cityPanelKey}
            handlePostalCodeChange={setPostalCode}
            onSetShouldFetchSchedules={setShouldFetchSchedules}
          />
        )}
      </ContentCol>
      <Content>
        {isLoading && <Loading loading={isLoading} />}
        {!error && filteredLocales.length > 0 && (
          <LocalesList>
            {filteredLocales
              .slice()
              .sort((a, b) => distanceKm(a.distance) - distanceKm(b.distance))
              .map((locale) => {
                const n = Number(locale.distance);
                const hasDistance =
                  locale.distance !== null &&
                  Number.isFinite(n) &&
                  n > 0 &&
                  n < DISTANCE_FALLBACK_KM;

                return (
                  <LocalesListItem
                    key={locale.codigo}
                    $isSelected={locale.codigo === local}
                    onClick={() => setLocal(locale.codigo)}
                  >
                    <p>
                      {filterVideoConference ? 'Videoconferência' : 'Presencial'} - {locale.bairro}{' '}
                      - {locale.cidade} - {locale.uf} - {locale.nomeFantasia} - {locale.endereco},{' '}
                      {locale.numero} - <b>{hasDistance ? `${n.toFixed(2)} km` : '—'}</b>
                    </p>
                  </LocalesListItem>
                );
              })}
          </LocalesList>
        )}
        {local && <ScheduleCalendar error={error} orderId={order?.id} localCode={local} />}
      </Content>
    </ContainerCol>
  );
};

export default ScheduleLocales;
